// src/pages/About/NossaEquipePage.jsx

import React from 'react';
import PropTypes from 'prop-types';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardMedia,
  IconButton,
  Divider,
  Paper,
  Button,
} from '@mui/material';
import {
  LinkedIn as LinkedInIcon,
  Email as EmailIcon,
  School as SchoolIcon,
  RocketLaunch as RocketIcon,
  Psychology as PsychologyIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import Footer from '../../components/Footer/Footer';

// Dados da equipe de liderança
const leadershipTeam = [
  {
    id: 1,
    name: "Danilo Taju",
    role: "CEO & Fundador",
    image: "/path/to/image.jpg", 
    description: "Visionario  em tecnologias educacionais, com vasta experiência no desenvolvimento de soluções inovadoras para o sector de ensino",
    linkedin: "https://linkedin.com/in/dataju",
    email: "dtaju@zenkroll.com"
  },
  {
    id: 2,
    name: "Amos Micas",
    role: "CTO",
    image: "/path/to/image.jpg",
    description: "Especialista em desenvolvimento de software e arquitetura de sistemas...",
    linkedin: "https://linkedin.com/in/micas",
    email: "amos@zenkroll.com"
  },
  // Adicione mais membros da liderança conforme necessário
];

// Dados dos departamentos
const departments = [
  {
    id: 1,
    name: "Desenvolvimento",
    members: [
      {
        name: "Jose Augusto",
        role: "Lead Developer",
        image: "/path/to/image.jpg"
      },
      {
        name: "Elisio Pedro",
        role: "Frontend Developer",
        image: "/path/to/image.jpg"
      },
      // Adicione mais membros conforme necessário
    ]
  },
  {
    id: 2,
    name: "Suporte ao Cliente",
    members: [
      {
        name: "Elias Manecas",
        role: "Customer Success Manager",
        image: "/path/to/image.jpg"
      },
      // Adicione mais membros conforme necessário
    ]
  },

];

// Componente para cartão de membro da liderança
const LeadershipCard = ({ member }) => (
  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
    <CardMedia
      component="img"
      height="300"
      image={member.image}
      alt={member.name}
      sx={{
        objectFit: 'cover',
        objectPosition: 'center',
      }}
    />
    <CardContent sx={{ flexGrow: 1 }}>
      <Typography variant="h5" gutterBottom>
        {member.name}
      </Typography>
      <Typography variant="subtitle1" color="primary" gutterBottom>
        {member.role}
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        {member.description}
      </Typography>
      <Box sx={{ mt: 2 }}>
        <IconButton href={member.linkedin} target="_blank" color="primary">
          <LinkedInIcon />
        </IconButton>
        <IconButton href={`mailto:${member.email}`} color="primary">
          <EmailIcon />
        </IconButton>
      </Box>
    </CardContent>
  </Card>
);

LeadershipCard.propTypes = {
  member: PropTypes.shape({
    image: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    linkedin: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
  }).isRequired,
};

// Componente para cartão de membro do departamento
const TeamMemberCard = ({ member }) => (
  <Card sx={{ height: '100%' }}>
    <CardMedia
      component="img"
      height="200"
      image={member.image}
      alt={member.name}
      sx={{ objectFit: 'cover' }}
    />
    <CardContent>
      <Typography variant="h6">
        {member.name}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {member.role}
      </Typography>
    </CardContent>
  </Card>
);

TeamMemberCard.propTypes = {
  member: PropTypes.shape({
    image: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    role: PropTypes.string.isRequired,
  }).isRequired,
};

const NossaEquipePage = () => {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          py: 8,
          mb: 6,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom align="center">
            Nossa Equipe
          </Typography>
          <Typography variant="h5" align="center" paragraph>
            Conheça as pessoas que fazem o ZenKroll acontecer
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ flex: 1 }}>
        {/* Valores da Equipe */}
        <Box sx={{ mb: 8 }}>
          <Grid container spacing={4}>
            {[
              {
                icon: <SchoolIcon sx={{ fontSize: 40 }} />,
                title: "Excelência Acadêmica",
                description: "Comprometidos com a qualidade da educação"
              },
              {
                icon: <RocketIcon sx={{ fontSize: 40 }} />,
                title: "Inovação Constante",
                description: "Sempre buscando novas soluções"
              },
              {
                icon: <PsychologyIcon sx={{ fontSize: 40 }} />,
                title: "Foco no Usuário",
                description: "Priorizando a experiência dos nossos usuários"
              }
            ].map((valor, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Paper 
                  sx={{ 
                    p: 3, 
                    textAlign: 'center',
                    height: '100%',
                    transition: 'transform 0.2s',
                    '&:hover': {
                      transform: 'translateY(-5px)'
                    }
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {valor.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom>
                    {valor.title}
                  </Typography>
                  <Typography color="text.secondary">
                    {valor.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Time de Liderança */}
        <Box sx={{ mb: 8 }}>
          <Typography variant="h3" align="center" gutterBottom>
            Time de Liderança
          </Typography>
          <Typography variant="subtitle1" align="center" color="text.secondary" paragraph>
            Conheça quem está liderando nossa missão de transformar a educação
          </Typography>
          <Grid container spacing={4} sx={{ mt: 4 }}>
            {leadershipTeam.map((member) => (
              <Grid item xs={12} md={4} key={member.id}>
                <LeadershipCard member={member} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Departamentos */}
        {departments.map((dept) => (
          <Box key={dept.id} sx={{ mb: 8 }}>
            <Typography variant="h4" gutterBottom>
              {dept.name}
            </Typography>
            <Divider sx={{ mb: 4 }} />
            <Grid container spacing={3}>
              {dept.members.map((member, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <TeamMemberCard member={member} />
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {/* Junte-se a Nós */}
        <Box 
          sx={{ 
            textAlign: 'center', 
            py: 8,
            mb: 6,
            bgcolor: 'grey.50',
            borderRadius: 2
          }}
        >
          <Typography variant="h4" gutterBottom>
            Faça Parte da Nossa Equipe
          </Typography>
          <Typography variant="subtitle1" paragraph sx={{ mb: 4 }}>
            Estamos sempre procurando talentos apaixonados por educação e tecnologia
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            size="large"
            href="/carreiras"
          >
            Ver Vagas Disponíveis
          </Button>
        </Box>
      </Container>

      <Footer />
    </Box>
  );
};

NossaEquipePage.propTypes = {};

export default NossaEquipePage;
